import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { CHATBOT_NAME_LIMIT } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { UnifiedAgent } from 'entities/Chatbot'
import { ChatbotRoutes } from 'entities/ExportRouters'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { TriggerSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/store/triggerSectionStore'
import { InstructionsSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsSectionStore'
import { BookingSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/store/bookingSectionStore'
import { AutoFollowUpSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/store/autoFollowUpSectionStore'
import { KnowledgeBaseSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/KnowledgeBaseSection/store/knowledgeBaseSectionStore'
import { chatbotOperation } from 'pages/chatbot/store/chatbotOperationStore'

export type IAgentConfigureTab = typeof ChatbotRoutes.chat | typeof ChatbotRoutes.config

export class UnifiedAgentStore {
  id: number | null
  readonly model: UnifiedAgent
  readonly nameInputStore: NameInputStore
  readonly triggerSectionStore: TriggerSectionStore
  readonly instructionsSectionStore: InstructionsSectionStore
  readonly bookingSectionStore: BookingSectionStore
  readonly autoFollowUpSectionStore: AutoFollowUpSectionStore
  readonly knowledgeBaseSectionStore: KnowledgeBaseSectionStore
  isSaving = false
  activeConfigureTab: IAgentConfigureTab = ChatbotRoutes.chat

  constructor(agentId: number | null, entities: IEntitiesStore) {
    this.id = agentId
    this.model = new UnifiedAgent()
    this.nameInputStore = new NameInputStore({
      name: '',
      limit: CHATBOT_NAME_LIMIT,
      isResetErrorOnChange: true,
    })
    this.triggerSectionStore = new TriggerSectionStore(entities, this.model.trigger)
    this.instructionsSectionStore = new InstructionsSectionStore(
      entities,
      agentId,
      this.model.instructions
    )
    this.bookingSectionStore = new BookingSectionStore(entities, this.model.booking)
    this.autoFollowUpSectionStore = new AutoFollowUpSectionStore(this.model.autoFollowUp)
    this.knowledgeBaseSectionStore = new KnowledgeBaseSectionStore(this.model.knowledgeBase)
    makeAutoObservable(this)
  }

  get isConfigureTab() {
    return this.activeConfigureTab === ChatbotRoutes.config
  }

  get payload() {
    return {
      ...this.model.toJSON(),
      name: this.nameInputStore.name,
      send_from: this.triggerSectionStore.sendFromStore.paramsSendFrom,
      filters: this.triggerSectionStore.paramsFilters,
      knowledge_base_documents: this.knowledgeBaseSectionStore.documents,
    }
  }

  setActiveConfigureTab = (tab: IAgentConfigureTab) => {
    this.activeConfigureTab = tab
  }

  setIsSaving = (isSaving: boolean) => {
    this.isSaving = isSaving
  }

  titleValidate = () => {
    if (!this.nameInputStore.name) {
      this.nameInputStore.setError('Please add a name for your AI Agent.')
      return false
    }
    return true
  }

  handleSaveAgent = async () => {
    this.setIsSaving(true)

    if (this.id) {
    } else {
      this.createAgent().then((id) => {
        this.setIsSaving(false)
        if (id) uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${id}` })
      })
    }
  }

  createAgent: (confirmTest?: boolean) => Promise<number | null> = async (confirmTest) => {
    if (!this.titleValidate()) return null

    if (confirmTest) {
      const confirmed = await chatbotOperation.confirmTest(
        this.nameInputStore.name,
        this.model.isActive
      )

      if (!confirmed) return null
    }

    try {
      const { data } = await chatbotOperation.create(this.payload)

      toastStore.add({
        title: 'AI Agent saved',
        type: 'info',
      })

      return runInAction(() => {
        this.id = data.id

        return data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error.response?.data?.errors ?? {})

      logger.error(error)
      toastStore.add({
        title: 'Something went wrong',
        desc: 'Please try again',
        type: 'error',
      })

      return null
    }
  }

  private _syncErrors = (errors: Record<string, string[]>) => {
    const sendFromError = errors['send_from']?.[0] ?? errors['send_from.options']?.[0]
    const instructionsError = errors['settings.instructions.value']?.[0]

    if (sendFromError) this.triggerSectionStore.setSendFromError(sendFromError)
    if (instructionsError) this.instructionsSectionStore.setError(instructionsError)
  }

  createAndPublishAgent: (confirmTest?: boolean) => Promise<number | null> = async () => {
    if (!this.titleValidate()) return null
    if (this.model.knowledgeBase.useKnowledgeBase) {
      const validateAgentKnowledgeBaseStore =
        this.knowledgeBaseSectionStore.agentKnowledgeBaseStore.checkValidate()
      if (!validateAgentKnowledgeBaseStore) return null
    }

    try {
      const { data } = await chatbotOperation.create({ ...this.payload, is_active: true })

      // if (data.is_active) await chatbotOperation.enroll(data.id, data.name, data.filters)

      return runInAction(() => {
        this.id = data.id

        return data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error.response?.data?.errors ?? {})

      logger.error(error)
      toastStore.add({
        title: 'Failed to activate',
        desc: 'Complete all required fields',
        type: 'error',
      })

      return null
    }
  }

  handlePublishAgent = async () => {
    this.setIsSaving(true)

    const isPublished = this.id ? () => {} : await this.createAndPublishAgent()
    this.setIsSaving(false)

    if (isPublished)
      uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}` })
  }

  dispose = () => {}
}
